import React from "react";
import { Field } from "react-final-form";
import {
  Box,
  FormHelperText,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Flx from "../../layout/Flx";
import RffInputWrapper from "../shared/RffInputWrapper";

/**
 * Boolean field. `variant`:
 *   "toggle" (default) — Yes / No button pair; leaves "unanswered" visible
 *   "switch"           — a switch with its state spelled out beside it
 */
const RffBooleanToggleField = ({
  name,
  label,
  required = false,
  helperText,
  row = false,
  suppressGrid,
  size,
  disabled,
  yesLabel = "Yes",
  noLabel = "No",
  variant = "toggle",
}) => (
  <RffInputWrapper
    label={label}
    suppressGrid={suppressGrid}
    size={size}
    helperText={helperText}
    required={required}
    row={row}
  >
    <Field
      name={name}
      validate={(v) =>
        required && v === undefined ? "This field is required" : undefined
      }
      render={({ input, meta }) => (
        <>
          {variant === "switch" ? (
            <Flx centerY gap={1.25} sx={{ minHeight: 40 }}>
              <Switch
                checked={input.value === true}
                onChange={(e) => input.onChange(e.target.checked)}
                disabled={disabled}
                size="small"
              />
              {/* A switch can't render "unanswered" — it's off either way — so
                  an untouched field says so in words rather than claiming "No". */}
              <Box
                component="span"
                sx={{
                  fontSize: 13.5,
                  color: "text.secondary",
                  userSelect: "none",
                  fontStyle: typeof input.value === "boolean" ? "normal" : "italic",
                }}
              >
                {typeof input.value === "boolean"
                  ? input.value
                    ? yesLabel
                    : noLabel
                  : "Not set"}
              </Box>
            </Flx>
          ) : (
            <ToggleButtonGroup
              exclusive
              color="primary"
              value={input.value}
              onChange={(_, next) => {
                if (next !== null) input.onChange(next);
              }}
              disabled={disabled}
              size="small"
              sx={{ "& .MuiToggleButton-root": { minWidth: 72 } }}
            >
              <ToggleButton value={true}>{yesLabel}</ToggleButton>
              <ToggleButton value={false}>{noLabel}</ToggleButton>
            </ToggleButtonGroup>
          )}
          {/* A toggle group never blurs on its own, so `touched` only arrives
              when the step gate touches every field on Continue — which is
              exactly when this needs to appear. */}
          {meta.touched && meta.error && (
            <FormHelperText error>{meta.error}</FormHelperText>
          )}
        </>
      )}
    />
  </RffInputWrapper>
);

export default RffBooleanToggleField;
